import { getYearsOfExperienceLabel } from '@/config/site';
import { skills, type SkillGroup } from '@/data/skills';
import { expertise, type Expertise } from '@/data/expertise';
import { achievements, type Achievement } from '@/data/achievements';
import { experience, type Experience } from '@/data/experience';
import { recommendations, type Recommendation } from '@/data/recommendations';

const yearsOfExperienceLabel = getYearsOfExperienceLabel();

/** A piece of prose: a plain string, or an inline link rendered by <RichText>. */
export type ContentSegment = string | { label: string; to: string };

export interface LinkItem {
  label: string;
  to: string;
  ariaLabel?: string;
}

export type SocialKey = 'email' | 'linkedin' | 'github';

export interface SocialChannel {
  /** Full href, incl. `mailto:` for email. */
  url: string;
  /** Accessible label for icon-only links. */
  label: string;
}

export interface SeoEntry {
  title: string;
  description: string;
  keywords: string;
}

/** A single instrument readout in the hero telemetry panel. */
export interface Metric {
  /** Sign shown before the value: '+', '−', or ''. */
  prefix: string;
  /** Target number the readout counts up to. */
  to: number;
  /** Fractional digits to render (e.g. 1 for 99.9). */
  decimals: number;
  /** Unit shown after the value, e.g. '%'. */
  suffix: string;
  /** Arrow direction; also the reading's semantic trend. */
  dir: 'up' | 'down';
  /** What the number measures. */
  label: string;
  /** Where it was measured. */
  caption: string;
}

/** Mono eyebrow labels for each section (paired with a structural index number). */
export interface SectionLabels {
  about: string;
  expertise: string;
  skills: string;
  experience: string;
  recommendations: string;
  contact: string;
}

export interface Content {
  social: Record<SocialKey, SocialChannel>;
  nav: { brand: string; links: LinkItem[] };
  hero: {
    imageAlt: string;
    /** Full descriptive string for aria / SEO fallback. */
    heading: string;
    /** Display nameplate. */
    name: string;
    /** Role line under the nameplate. */
    role: string;
    /** Mono spec: primary stack, shown first in the spec strip. */
    stack: string;
    /** Mono spec: location, kept on one line (never wraps mid-place). */
    location: string;
    /** One-sentence thesis. */
    statement: string;
    /** Availability indicator text next to the live dot. */
    liveLabel: string;
    /** Eyebrow above the telemetry panel — frames the metrics as career-spanning, not current-role. */
    metricsLabel: string;
    metrics: Metric[];
    ctaPrimary: LinkItem;
    ctaSecondary: LinkItem;
    /** Quiet footnote cue under the telemetry panel, linking to the endorsements section. */
    endorsementCue: LinkItem;
  };
  sections: SectionLabels;
  about: { heading: string; body: ContentSegment[] };
  expertise: { heading: string; intro: ContentSegment[]; items: Expertise[] };
  achievements: { heading: string; intro: ContentSegment[]; items: Achievement[] };
  skills: { heading: string; groups: SkillGroup[] };
  experience: { heading: string; roles: Experience[] };
  recommendations: {
    heading: string;
    intro: ContentSegment[];
    items: Recommendation[];
    linkedInUrl: string;
  };
  contact: {
    heading: string;
    intro: ContentSegment[];
    infoHeading: string;
    channels: { type: SocialKey; text: string }[];
    /** Label/target for the floating cue that jumps to this section mid-scroll. */
    jumpCue: LinkItem;
  };
  notFound: {
    title: string;
    subtitle: string;
    body: string;
    ctaHome: LinkItem;
    ctaContact: LinkItem;
    popularHeading: string;
    popularLinks: LinkItem[];
  };
  footer: { tagline: string };
  seo: { default: SeoEntry; notFound: SeoEntry };
}

export const content: Content = {
  social: {
    email: { url: 'mailto:karam.khoury88@gmail.com', label: 'Email Karam Khoury' },
    linkedin: { url: 'https://www.linkedin.com/in/karamkhoury88/', label: 'Karam Khoury on LinkedIn' },
    github: { url: 'https://github.com/karamkhoury88', label: 'Karam Khoury on GitHub' },
  },

  nav: {
    brand: 'Karam Khoury',
    // Path-qualified: the header also renders on the 404 route, where a bare
    // `#contact` would only set the hash instead of returning to the section.
    links: [{ label: 'Contact', to: '/#contact', ariaLabel: 'Jump to the contact section' }],
  },

  hero: {
    imageAlt: 'Karam Khoury, Principal Software Engineer and Solution Architect',
    heading: 'Karam Khoury — Principal Software Engineer & Solution Architect',
    name: 'Karam Khoury',
    role: 'Principal Software Engineer & Solution Architect',
    stack: '.NET · Azure · PostGIS · Camunda',
    location: 'Dubai, UAE',
    statement:
      `${yearsOfExperienceLabel} years architecting enterprise systems on the Microsoft stack — modernizing legacy government platforms, building geospatial GIS platforms, and shipping payments that move real money at scale.`,
    liveLabel: 'Ready for the next challenge',
    metricsLabel: 'Verified track record — across the career',
    metrics: [
      { prefix: '+', to: 50, decimals: 0, suffix: '%', dir: 'up', label: 'Transaction volume', caption: 'Klip digital wallet' },
      { prefix: '', to: 99.9, decimals: 1, suffix: '%', dir: 'up', label: 'Service uptime', caption: 'Azure, sustained' },
      { prefix: '−', to: 40, decimals: 0, suffix: '%', dir: 'down', label: 'Security incidents', caption: 'OWASP-aligned' },
      { prefix: '−', to: 90, decimals: 0, suffix: '%', dir: 'down', label: 'Onboarding time', caption: 'Digital KYC' },
    ],
    // ariaLabel must CONTAIN the visible label (WCAG 2.5.3 Label in Name) so voice-control
    // users can say "click Get in touch" and have it match.
    ctaPrimary: {
      label: 'Get in touch',
      to: '#contact',
      ariaLabel: 'Get in touch — jump to the contact section',
    },
    ctaSecondary: { label: 'Read the career ledger', to: '#career', ariaLabel: 'Jump to career history' },
    endorsementCue: {
      label: 'The people who led those systems, in their words',
      to: '#recommendations',
      ariaLabel: 'Jump to endorsements from senior leaders',
    },
  },

  sections: {
    about: 'Profile',
    expertise: 'Capabilities',
    skills: 'Stack',
    experience: 'Career ledger',
    recommendations: 'Endorsements',
    contact: 'Contact',
  },

  about: {
    heading: 'About Me - Principal Software Engineer & Solution Architect',
    body: [
      `Principal Software Engineer and Solution Architect with ${yearsOfExperienceLabel} years building, securing, and modernizing enterprise systems on the Microsoft stack. Led backend architecture for the modernization of a legacy Dubai government real estate registration platform onto containerized .NET 10 with Camunda 8 workflow orchestration, and is now architecting a greenfield geospatial platform on PostGIS, Esri ArcGIS, and vector tiles. Five years before that in UAE digital wallet and payment platforms, increasing transaction volume by 50% and reducing security incidents by 40%. Certified Scrum Master (PSM I), CMI Team Leadership, and Engineering Management (Coursera). `,
      { label: 'Get in touch', to: '#contact' },
      ' to discuss how I can help with your next project.',
    ],
  },

  expertise: {
    heading: 'Core Expertise - Solution Architecture & Modernization',
    intro: [
      `With ${yearsOfExperienceLabel} years of experience, I specialize in modernizing legacy platforms and architecting geospatial and enterprise systems. `,
      { label: "Let's discuss your project", to: '#contact' },
      '.',
    ],
    items: expertise,
  },

  achievements: {
    heading: 'Recent Achievements - Software Engineering Success',
    intro: [
      'These achievements demonstrate my commitment to delivering high-quality solutions. Interested in working together? ',
      { label: 'Contact me', to: '#contact' },
      ' to discuss your project.',
    ],
    items: achievements,
  },

  skills: {
    heading: 'Technical Skills - .NET, Azure, C# & More',
    groups: skills,
  },

  experience: {
    heading: 'Professional Journey - Software Engineering Career',
    roles: experience,
  },

  recommendations: {
    heading: 'What Colleagues Say - Verified LinkedIn Recommendations',
    intro: [
      'Endorsements from the leaders, directors, and teammates I have worked alongside throughout my career. ',
      { label: 'Read the full career ledger', to: '#career' },
      '.',
    ],
    items: recommendations,
    linkedInUrl: 'https://www.linkedin.com/in/karamkhoury88/',
  },

  contact: {
    heading: 'Get in Touch',
    intro: [
      "I'm open to principal/architect roles, modernization programmes, and platform work — anywhere systems have to reconcile under real load. Email reaches me fastest.",
    ],
    infoHeading: 'Contact Information',
    channels: [
      { type: 'email', text: 'karam.khoury88@gmail.com' },
      { type: 'linkedin', text: 'karamkhoury88' },
      { type: 'github', text: 'karamkhoury88' },
    ],
    // See ctaPrimary: the accessible name has to contain the visible "Get in touch".
    jumpCue: {
      label: 'Get in touch',
      to: '#contact',
      ariaLabel: 'Get in touch — jump to the contact section',
    },
  },

  notFound: {
    title: '404',
    subtitle: 'Page Not Found',
    body: "The page you're looking for doesn't exist or has been moved. Let's get you back on track.",
    ctaHome: { label: 'Go to Homepage', to: '/' },
    // Cross-page targets: a bare `#contact` would only set the hash on the 404 URL.
    ctaContact: { label: 'Contact Me', to: '/#contact' },
    popularHeading: 'Popular Pages',
    popularLinks: [
      { label: 'Home - Software Engineer Portfolio', to: '/' },
      { label: 'Contact - Get in Touch', to: '/#contact' },
    ],
  },

  footer: {
    tagline: 'Principal Software Engineer & Solution Architect',
  },

  seo: {
    default: {
      title: 'Karam Khoury — Principal Software Engineer & Architect',
      description:
        `${yearsOfExperienceLabel} years as Principal Software Engineer & Solution Architect modernizing legacy platforms, GIS & fintech on .NET & Azure. +50% transactions, -40% incidents.`,
      keywords:
        'Karam Khoury, Software Engineer, Solution Architect, .NET Developer, Azure Expert, C# Developer, ASP.NET Core, Microservices, Software Architect, Technical Leader, Legacy Modernization, Camunda, BPMN, PostGIS, Esri ArcGIS, GIS Developer, Oracle, Keycloak, Government Systems Dubai, UAE Software Engineer, .NET Expert, Contact Karam Khoury',
    },
    notFound: {
      title: '404 - Page Not Found | Karam Khoury Portfolio',
      description:
        "The page you're looking for doesn't exist. Return to Karam Khoury's portfolio homepage to explore software engineering expertise, .NET development, and Azure cloud solutions.",
      keywords: '404, Page Not Found, Karam Khoury',
    },
  },
};
